// In-depth HTML lessons (Codeazy lesson standard).

export const htmlLessons = [
  {
    slug: "document-structure",
    title: "How Web Pages Work",
    summary: "What happens between typing a URL and seeing a page, and the HTML skeleton every page needs.",
    readTime: 10,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Every website, from a simple portfolio to a large React app, ends up as HTML in the browser. Knowing how the browser turns HTML into pixels helps you write pages that load fast, work on phones and can be read by search engines and screen readers. “What happens when you type a URL?” is also a classic interview question." },

      { type: "h2", text: "The mental model: skeleton, skin and muscles" },
      { type: "table", headers: ["Layer", "Role", "Analogy"], rows: [
        ["HTML", "Structure and meaning: headings, paragraphs, links, forms", "The skeleton"],
        ["CSS", "Presentation: colours, layout, fonts, animation", "The skin and clothes"],
        ["JavaScript", "Behaviour: reacting to clicks, fetching data, updating the page", "The muscles"]
      ] },
      { type: "p", text: "A page should still make sense with only its skeleton. If you turn off CSS, the content and its order should still be readable. That is a good test of well-written HTML." },

      { type: "h2", text: "From URL to pixels" },
      { type: "ol", items: [
        "**DNS lookup:** the browser finds the server’s IP address for `codeazy.in`.",
        "**Request:** it sends an HTTP request, and the server responds with an HTML file.",
        "**Parse HTML → DOM:** the browser reads the HTML top to bottom and builds the **DOM**, a tree of objects representing every element.",
        "**Parse CSS → CSSOM:** stylesheets are turned into another tree of style rules.",
        "**Render tree and layout:** the DOM and CSSOM are combined, and the browser calculates the size and position of every visible element.",
        "**Paint:** pixels are drawn on screen. JavaScript can then change the DOM, which may trigger layout and paint again."
      ] },
      { type: "callout", variant: "note", title: "What is the DOM?", text: "The DOM (Document Object Model) is the browser’s live, in-memory tree of your page. HTML is the text file, and the DOM is what JavaScript reads and changes, for example with `document.querySelector`." },

      { type: "h2", text: "The skeleton every page needs" },
      { type: "code", language: "html", code: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Asha Rao · Frontend Developer</title>
    <link rel="stylesheet" href="styles.css" />
    <script src="app.js" defer></script>
  </head>
  <body>
    <h1>Asha Rao</h1>
    <p>I build fast, accessible websites.</p>
  </body>
</html>` },
      { type: "table", headers: ["Line", "Why it’s there"], rows: [
        ["`<!DOCTYPE html>`", "Tells the browser to use standards mode. Without it, browsers use “quirks mode” and layouts can break in odd ways."],
        ["`<html lang=\"en\">`", "The root element. `lang` helps screen readers pronounce words correctly and helps search engines and translation tools."],
        ["`<meta charset=\"UTF-8\">`", "Lets the page display every language and emoji correctly."],
        ["`<meta name=\"viewport\">`", "Makes the page fit the phone’s screen width. Without it, mobile browsers show a zoomed-out desktop page."],
        ["`<title>`", "Shown in the browser tab, bookmarks and search results."],
        ["`<script defer>`", "Downloads JavaScript without blocking HTML parsing, and runs it after the document is parsed."]
      ] },

      { type: "h2", text: "Elements, attributes and nesting" },
      { type: "code", language: "html", code: `<!-- element with attributes -->
<a href="https://codeazy.in/learn" target="_blank" rel="noopener">Start learning</a>

<!-- void elements have no closing tag -->
<img src="avatar.jpg" alt="Asha smiling at her desk" />
<br />
<input type="email" />

<!-- nesting: close tags in reverse order of opening -->
<p>Learn <strong>HTML</strong> first.</p>` },
      { type: "h3", text: "Block vs inline elements" },
      { type: "table", headers: ["", "Block", "Inline"], rows: [
        ["Starts on a new line", "Yes", "No"],
        ["Takes the full available width", "Yes", "Only as wide as its content"],
        ["Respects width and height", "Yes", "No"],
        ["Examples", "`div`, `p`, `h1`–`h6`, `ul`, `section`, `form`", "`span`, `a`, `strong`, `em`, `img`, `code`"]
      ] },

      { type: "h2", text: "Where to put scripts: async vs defer" },
      { type: "table", headers: ["Attribute", "Blocks HTML parsing?", "When it runs", "Order kept?"], rows: [
        ["None (in `<head>`)", "Yes, while it downloads and runs", "Immediately", "Yes"],
        ["`async`", "No", "As soon as it has downloaded", "No"],
        ["`defer`", "No", "After the HTML is parsed, before `DOMContentLoaded`", "Yes"]
      ] },
      { type: "p", text: "Use `defer` for your app code. Use `async` for independent scripts such as analytics, where order doesn’t matter." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Blocking script in the head", bad: `<head>
  <script src="app.js"></script>
</head>
<!-- page stays blank while app.js
     downloads and runs, and
     document.querySelector finds nothing -->`, goodLabel: "Deferred script", good: `<head>
  <script src="app.js" defer></script>
</head>
<!-- HTML parses immediately,
     app.js runs once the DOM is ready -->` },
      { type: "compare", badLabel: "Invalid nesting", bad: `<p>
  Our courses:
  <div>DSA, System Design</div>
</p>`, goodLabel: "Valid structure", good: `<p>Our courses:</p>
<div>DSA, System Design</div>`, text: "A `<p>` cannot contain block elements such as `<div>`. The browser closes the paragraph early, which produces a different DOM from what you expect." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What happens when you type a URL into the browser and press Enter?", answer: ["The browser resolves the domain with DNS, opens a connection (with a TLS handshake for HTTPS) and sends an HTTP request. The server responds with HTML. The browser parses the HTML into the DOM and the CSS into the CSSOM, combines them into a render tree, calculates layout, paints pixels and runs JavaScript, which may update the DOM again."] },
      { type: "qa", question: "What is the purpose of `<!DOCTYPE html>`?", answer: ["It tells the browser the document is HTML5 and should be rendered in standards mode. Without it, browsers fall back to quirks mode, which emulates old, inconsistent rendering behaviour."] },
      { type: "qa", question: "What is the difference between `async` and `defer`?", answer: ["Both download the script without blocking parsing. `async` runs the script as soon as it has downloaded, in no guaranteed order, possibly before parsing finishes. `defer` runs scripts in document order after parsing completes, so they can safely use the DOM."] },
      { type: "qa", question: "What is the difference between HTML and the DOM?", answer: ["HTML is the source text sent by the server. The DOM is the live tree of objects the browser builds from it. JavaScript changes the DOM, not the HTML file. Browser DevTools show the current DOM, which can differ from the original HTML."] },

      { type: "exercise", title: "Build a profile page skeleton", task: "Create a complete HTML page for a developer profile. It needs a correct head (charset, viewport, a descriptive title), a main heading with your name, a short bio paragraph, a profile photo with good alt text, and a link to your GitHub that opens in a new tab safely.", language: "html", solution: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Ravi Kumar · Full-Stack Developer</title>
  </head>
  <body>
    <h1>Ravi Kumar</h1>
    <img src="ravi.jpg" alt="Ravi Kumar wearing a blue shirt, smiling" width="160" height="160" />
    <p>Full-stack developer who loves building fast web apps with React and Node.js.</p>
    <a href="https://github.com/ravikumar" target="_blank" rel="noopener noreferrer">
      View my GitHub
    </a>
  </body>
</html>`, explanation: "`rel=\"noopener noreferrer\"` stops the new tab from getting access to your page through `window.opener`. Setting `width` and `height` on images lets the browser reserve space before the image loads, which prevents layout shift." },

      { type: "summary", items: [
        "HTML is structure, CSS is presentation and JavaScript is behaviour.",
        "The browser builds the DOM from HTML and the CSSOM from CSS, then calculates layout and paints.",
        "Every page needs a doctype, `lang`, charset, viewport and a descriptive title.",
        "Use `defer` for app scripts so they don’t block rendering.",
        "Respect the nesting rules: block elements don’t go inside `<p>`, and void elements have no closing tag."
      ] }
    ]
  },

  {
    slug: "text-links-lists-tables",
    title: "Text, Links, Lists and Tables",
    summary: "The everyday building blocks: formatting text correctly, linking pages, structuring lists and presenting data in tables.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Most of any web page is text, links, lists and the occasional table. Choosing the right element for each affects how the page reads aloud, how it ranks, how it copies and pastes, and how easy it is to style. These are also the elements beginners most often get subtly wrong." },

      { type: "h2", text: "The mental model: a well-edited document" },
      { type: "p", text: "Write HTML the way a good editor formats a document: a title, section headings, paragraphs, emphasis where it changes meaning, bullet lists for groups, numbered lists for steps and tables only for real tabular data. If you would use a feature in a well-formatted Word document, there is usually a matching HTML element." },

      { type: "h2", text: "Text-level elements" },
      { type: "table", headers: ["Element", "Meaning", "Example use"], rows: [
        ["`<strong>`", "Strong importance", "“**Never** share your OTP”"],
        ["`<em>`", "Stress emphasis that changes meaning", "“I **did** submit it” (said with stress)"],
        ["`<b>` / `<i>`", "Styled text with no extra importance", "Product names, foreign words, technical terms"],
        ["`<mark>`", "Highlighted or relevant text", "Search matches"],
        ["`<code>`", "A fragment of code", "“Call `map()` on the array”"],
        ["`<pre>`", "Preformatted text that keeps whitespace", "Code blocks, ASCII diagrams"],
        ["`<kbd>`", "Keyboard input", "`<kbd>Ctrl</kbd> + <kbd>S</kbd>`"],
        ["`<abbr title>`", "Abbreviation with its full form", "`<abbr title=\"Data Structures and Algorithms\">DSA</abbr>`"],
        ["`<time datetime>`", "A machine-readable date or time", "`<time datetime=\"2026-11-09\">9 Nov</time>`"],
        ["`<blockquote>` / `<q>`", "Block or inline quotations", "Testimonials"],
        ["`<br>`", "A line break that is part of the content", "Addresses, poems. Not for spacing"]
      ] },
      { type: "callout", variant: "tip", text: "`<strong>` and `<b>` look the same by default, but `<strong>` carries meaning. Ask: if someone read this aloud, would they stress it? If yes, use `<strong>` or `<em>`." },

      { type: "h3", text: "Whitespace and entities" },
      { type: "p", text: "HTML collapses runs of spaces and newlines into a single space, except inside `<pre>`. Characters that have special meaning in HTML must be written as **entities**:" },
      { type: "table", headers: ["Character", "Entity", "Why"], rows: [
        ["`<`", "`&lt;`", "Otherwise it starts a tag"],
        ["`>`", "`&gt;`", "Closes a tag"],
        ["`&`", "`&amp;`", "Starts an entity"],
        ["`\"`", "`&quot;`", "Inside attribute values"],
        ["Non-breaking space", "`&nbsp;`", "Keeps “10 km” on one line"],
        ["`©`", "`&copy;`", "Convenience (UTF-8 also works directly)"]
      ] },

      { type: "h2", text: "Links in depth" },
      { type: "code", language: "html", code: `<!-- Absolute URL: another site -->
<a href="https://developer.mozilla.org">MDN Web Docs</a>

<!-- Root-relative: same site, from the root -->
<a href="/learn/javascript">JavaScript lessons</a>

<!-- Relative: from the current folder -->
<a href="../blog/index.html">Blog</a>

<!-- Jump to a section with an id on the same page -->
<a href="#interview-questions">Skip to interview questions</a>
<h2 id="interview-questions">Interview questions</h2>

<!-- Email, phone and download links -->
<a href="mailto:hello@codeazy.in">hello@codeazy.in</a>
<a href="tel:+919876543210">+91 98765 43210</a>
<a href="/syllabus.pdf" download>Download syllabus (PDF, 2 MB)</a>

<!-- New tab, safely -->
<a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub (opens in new tab)</a>` },
      { type: "table", headers: ["URL type", "Example", "Resolves from"], rows: [
        ["Absolute", "`https://site.com/a`", "Nowhere; it is complete"],
        ["Root-relative", "`/learn/css`", "The site root"],
        ["Relative", "`images/logo.png`, `../index.html`", "The current page’s folder"],
        ["Fragment", "`#pricing`", "The current page"]
      ] },
      { type: "callout", variant: "warning", text: "Opening links in new tabs surprises users and breaks the Back button. Do it only when leaving would lose work (for example, a link inside a form), and say so in the link text." },

      { type: "h2", text: "Lists" },
      { type: "code", language: "html", code: `<!-- Unordered: order doesn't matter -->
<ul>
  <li>HTML</li>
  <li>CSS</li>
  <li>JavaScript</li>
</ul>

<!-- Ordered: steps or rankings -->
<ol start="1">
  <li>Install Node.js</li>
  <li>Run <code>npm create vite@latest</code></li>
  <li>Start the dev server</li>
</ol>

<!-- Nested list: the inner list goes INSIDE an li -->
<ul>
  <li>Frontend
    <ul>
      <li>React</li>
      <li>Next.js</li>
    </ul>
  </li>
</ul>

<!-- Description list: term and definition pairs -->
<dl>
  <dt>Closure</dt>
  <dd>A function bundled with references to its surrounding scope.</dd>
  <dt>Hoisting</dt>
  <dd>Declarations being processed before code runs.</dd>
</dl>` },
      { type: "p", text: "Navigation menus are lists of links, which is why `<nav>` usually contains a `<ul>`. Screen readers announce “list, 5 items”, which tells users how big the menu is." },

      { type: "h2", text: "Tables for tabular data" },
      { type: "p", text: "Use tables when data has rows **and** columns that relate to each other, such as a timetable, a price comparison or marks. Don’t use tables for page layout; that is what CSS Grid and Flexbox are for." },
      { type: "code", language: "html", code: `<table>
  <caption>Upcoming live cohorts</caption>
  <thead>
    <tr>
      <th scope="col">Course</th>
      <th scope="col">Starts</th>
      <th scope="col">Schedule</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <th scope="row">DSA</th>
      <td><time datetime="2026-11-16">16 Nov 2026</time></td>
      <td>Mon, Wed, Fri</td>
    </tr>
    <tr>
      <th scope="row">System Design</th>
      <td><time datetime="2026-12-05">5 Dec 2026</time></td>
      <td>Sat &amp; Sun</td>
    </tr>
  </tbody>
</table>` },
      { type: "table", headers: ["Part", "Purpose"], rows: [
        ["`<caption>`", "The table’s title, announced first by screen readers"],
        ["`<thead>` / `<tbody>` / `<tfoot>`", "Group header, body and footer rows"],
        ["`<th scope=\"col\">`", "A column header, so each cell is read with its column name"],
        ["`<th scope=\"row\">`", "A row header, such as the course name"],
        ["`colspan` / `rowspan`", "Merge cells across columns or rows (use sparingly)"]
      ] },
      { type: "callout", variant: "tip", text: "Wide tables break small screens. Wrap them in a container with `overflow-x: auto` so the table scrolls sideways instead of stretching the page." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "br for spacing", bad: `<p>Welcome to Codeazy</p>
<br /><br /><br />
<p>Start learning today</p>`, goodLabel: "CSS for spacing", good: `<p>Welcome to Codeazy</p>
<p class="mt-lg">Start learning today</p>

/* CSS */
.mt-lg { margin-top: 3rem; }` },
      { type: "compare", badLabel: "Fake list", bad: `<p>- HTML<br />- CSS<br />- JavaScript</p>`, goodLabel: "Real list", good: `<ul>
  <li>HTML</li>
  <li>CSS</li>
  <li>JavaScript</li>
</ul>` },
      { type: "compare", badLabel: "Nested list outside li", bad: `<ul>
  <li>Frontend</li>
  <ul>
    <li>React</li>
  </ul>
</ul>`, goodLabel: "Nested list inside li", good: `<ul>
  <li>Frontend
    <ul>
      <li>React</li>
    </ul>
  </li>
</ul>`, text: "A `<ul>` may only contain `<li>` elements directly. The nested list belongs inside the item it describes." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between `<strong>` and `<b>`, and `<em>` and `<i>`?", answer: ["`<strong>` and `<em>` carry meaning: strong importance and stress emphasis. Screen readers may change tone for them. `<b>` and `<i>` only offset text stylistically without extra importance, for example a product name or a term in another language. All four look similar by default."] },
      { type: "qa", question: "When should you use a table?", answer: ["Only for tabular data, where each cell relates to both its row and its column. Use `<caption>`, `<th>` with `scope`, and `<thead>`/`<tbody>` so the relationships are accessible. Never use tables for layout."] },
      { type: "qa", question: "What is the difference between relative and absolute URLs?", answer: ["An absolute URL includes the protocol and domain and works from anywhere. A root-relative URL starting with `/` resolves from the site root. A relative URL resolves from the current page’s location, so it can break if the page moves. Root-relative URLs are usually the safest choice for links within a site."] },
      { type: "qa", question: "Why should `<br>` not be used for spacing?", answer: ["`<br>` is a line break that is part of the content, such as in an address or a poem. Using it for spacing mixes presentation into the structure, gives inconsistent results and adds noise for screen readers. Use CSS margins and padding instead."] },
      { type: "qa", question: "What does `<dl>` represent?", answer: ["A description list of name and value groups: terms (`<dt>`) with one or more descriptions (`<dd>`). It suits glossaries, FAQs and metadata such as “Duration: 3 months”."] },

      { type: "exercise", title: "Build a course comparison table", task: "Build an accessible table comparing three courses (Full-Stack, DSA, System Design) by duration, level and number of modules. Include a caption, column headers, row headers and a table footer that states the total number of modules.", language: "html", solution: `<table>
  <caption>Codeazy course comparison</caption>
  <thead>
    <tr>
      <th scope="col">Course</th>
      <th scope="col">Duration</th>
      <th scope="col">Level</th>
      <th scope="col">Modules</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <th scope="row">Full-Stack</th>
      <td>6 months</td>
      <td>Beginner → Advanced</td>
      <td>8</td>
    </tr>
    <tr>
      <th scope="row">DSA</th>
      <td>4 months</td>
      <td>All levels</td>
      <td>11</td>
    </tr>
    <tr>
      <th scope="row">System Design</th>
      <td>3 months</td>
      <td>Intermediate</td>
      <td>10</td>
    </tr>
  </tbody>
  <tfoot>
    <tr>
      <th scope="row" colspan="3">Total modules</th>
      <td>29</td>
    </tr>
  </tfoot>
</table>`, explanation: "With `scope` set on the header cells, a screen reader announces a cell like “DSA, Duration, 4 months”, so the data still makes sense without seeing the grid." },

      { type: "summary", items: [
        "Pick text elements by meaning: `<strong>`/`<em>` for importance and stress, `<code>`, `<kbd>`, `<time>` and `<abbr>` for specific content.",
        "Escape `<`, `>` and `&` as entities, and remember HTML collapses whitespace outside `<pre>`.",
        "Know absolute, root-relative, relative and fragment URLs, and open new tabs sparingly and safely.",
        "Use `<ul>` for groups, `<ol>` for sequences and `<dl>` for term–definition pairs. Nested lists go inside an `<li>`.",
        "Tables are for tabular data only, with `<caption>`, `<th scope>` and row groups."
      ] }
    ]
  },

  {
    slug: "semantic-html",
    title: "Semantic HTML and Accessibility",
    summary: "Use elements that describe meaning, so your pages work for everyone, including screen readers and search engines.",
    readTime: 11,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Many people use the web with screen readers, keyboards only or zoomed-in screens. Search engines “read” your page much like a screen reader does. Semantic HTML makes your site usable for all of them with very little extra effort. Many companies are also legally required to meet accessibility standards, so this skill is valued in interviews." },

      { type: "h2", text: "The mental model: labelled boxes" },
      { type: "p", text: "Imagine moving house. A box labelled “Kitchen – plates” is far more useful than a box labelled “box”. A `<div>` is an unlabelled box. `<nav>`, `<main>` and `<button>` are labelled boxes. Browsers, assistive technology and search engines all read those labels." },

      { type: "h2", text: "Page landmarks" },
      { type: "code", language: "html", code: `<body>
  <header>
    <a href="/" class="logo">Codeazy</a>
    <nav aria-label="Main">
      <ul>
        <li><a href="/learn">Learn</a></li>
        <li><a href="/blog">Blog</a></li>
      </ul>
    </nav>
  </header>

  <main>
    <article>
      <h1>Understanding Closures</h1>
      <p>A closure is a function that remembers...</p>
      <section>
        <h2>Real-world uses</h2>
        <p>...</p>
      </section>
    </article>
    <aside>
      <h2>Related lessons</h2>
    </aside>
  </main>

  <footer>© 2026 Codeazy</footer>
</body>` },
      { type: "table", headers: ["Element", "Use it for", "Screen readers announce it as"], rows: [
        ["`<header>`", "Site header or the introduction of a section", "Banner (when it is the page header)"],
        ["`<nav>`", "Major blocks of navigation links", "Navigation"],
        ["`<main>`", "The unique main content, once per page", "Main"],
        ["`<article>`", "Self-contained content: a post, a product card, a comment", "Article"],
        ["`<section>`", "A themed group of content with its own heading", "Region (when labelled)"],
        ["`<aside>`", "Related but secondary content", "Complementary"],
        ["`<footer>`", "Footer of the page or a section", "Content info (when it is the page footer)"]
      ] },
      { type: "callout", variant: "tip", text: "Screen reader users jump between landmarks and headings the way sighted users skim a page. A correct `<main>` and a sensible heading outline are the two biggest accessibility wins." },

      { type: "h2", text: "Headings form an outline" },
      { type: "compare", badLabel: "Headings chosen for size", bad: `<h1>Codeazy</h1>
<h4>Learn JavaScript</h4>   <!-- skipped levels -->
<h2>Closures</h2>
<h1>Event loop</h1>          <!-- second h1 -->`, goodLabel: "Headings follow structure", good: `<h1>Learn JavaScript</h1>
  <h2>Closures</h2>
    <h3>Private state</h3>
  <h2>Event loop</h2>`, text: "Choose heading levels for structure, then use CSS to change their size. Don’t skip levels, and use a single `<h1>` that describes the page." },

      { type: "h2", text: "Use the right interactive element" },
      { type: "p", text: "This is the most common accessibility bug in real projects:" },
      { type: "compare", badLabel: "A div pretending to be a button", bad: `<div class="btn" onclick="enroll()">
  Enroll now
</div>`, goodLabel: "A real button", good: `<button type="button" onclick="enroll()">
  Enroll now
</button>`, text: "The `<div>` can’t be reached with Tab, doesn’t respond to Enter or Space, and isn’t announced as a button. A `<button>` gets all of this for free." },
      { type: "table", headers: ["If it…", "Use"], rows: [
        ["Navigates to another page or URL", "`<a href=\"...\">`"],
        ["Performs an action on this page (submit, open, toggle)", "`<button>`"],
        ["Collects input", "`<input>`, `<select>`, `<textarea>` with a `<label>`"]
      ] },

      { type: "h2", text: "Images and alt text" },
      { type: "ul", items: [
        "Describe what the image communicates in context, not just what it is: “Bar chart showing placements rising from 60% to 92%”.",
        "Decorative images get an empty alt, `alt=\"\"`, so screen readers skip them.",
        "Don’t start with “Image of”, because screen readers already announce it as an image.",
        "If an image is a link, the alt text should describe where the link goes."
      ] },

      { type: "h2", text: "ARIA: the last resort" },
      { type: "p", text: "ARIA attributes add accessibility information when HTML alone can’t express it, for example `aria-expanded` on a menu toggle or `aria-live` for status messages. The first rule of ARIA is: **if a native HTML element does the job, use it instead.**" },
      { type: "code", language: "html", code: `<button aria-expanded="false" aria-controls="menu">Menu</button>
<ul id="menu" hidden>...</ul>

<!-- Announce form results to screen readers -->
<p role="status" aria-live="polite">Your request was sent.</p>

<!-- Icon-only button needs an accessible name -->
<button aria-label="Close dialog">✕</button>` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is semantic HTML and why does it matter?", answer: ["Semantic HTML uses elements that describe the meaning of content, such as `nav`, `main`, `article`, `button` and headings, instead of generic `div`s and `span`s. It gives assistive technology and search engines the structure of the page, provides keyboard behaviour for free, and makes code easier to read."] },
      { type: "qa", question: "When would you use `<section>` vs `<article>` vs `<div>`?", answer: ["`<article>` is self-contained content that would make sense on its own, such as a blog post or product card. `<section>` groups related content under a heading within a page. `<div>` has no meaning and is used only as a styling or layout wrapper."] },
      { type: "qa", question: "How do you make an icon-only button accessible?", answer: ["Use a real `<button>`, and give it an accessible name with `aria-label`, for example “Close dialog”, or with visually hidden text. Also make sure it has a visible focus style."] },
      { type: "qa", question: "How would you test a page for accessibility?", answer: ["Navigate it using only the keyboard. Check the heading outline and landmarks. Run Lighthouse or axe DevTools. Try a screen reader such as VoiceOver or NVDA on key flows. Check colour contrast and that the page works at 200% zoom."] },

      { type: "exercise", title: "Fix the inaccessible card", task: "Rewrite this course card using semantic HTML. It should be keyboard accessible, the image needs proper alt text, and the heading should fit in a page where the main heading is `<h1>Courses</h1>`.", language: "html", starter: `<div class="card">
  <img src="dsa.png">
  <div class="title">Data Structures & Algorithms</div>
  <div class="desc">Crack coding interviews with patterns.</div>
  <div class="btn" onclick="openCourse('dsa')">View course</div>
</div>`, solution: `<article class="card">
  <img src="dsa.png" alt="" />
  <h2>Data Structures &amp; Algorithms</h2>
  <p>Crack coding interviews with patterns.</p>
  <a href="/courses/dsa" class="btn">View course</a>
</article>`, explanation: "The card is self-contained, so it is an `<article>`. The image is decorative because the heading already names the course, so it gets `alt=\"\"`. The title is an `<h2>` under the page’s `<h1>`. Because the button goes to another page, it becomes a link, which is focusable and announced correctly." },

      { type: "summary", items: [
        "Semantic elements are labelled boxes that browsers, screen readers and search engines understand.",
        "Use one `<main>`, a single descriptive `<h1>` and headings that don’t skip levels.",
        "Links navigate and buttons act. Never make clickable `<div>`s.",
        "Write alt text for meaning in context, and use `alt=\"\"` for decorative images.",
        "Reach for ARIA only when native HTML can’t express what you need."
      ] }
    ]
  },

  {
    slug: "forms",
    title: "Forms and Validation",
    summary: "Build forms that are easy to use, accessible, and validated before they reach your server.",
    readTime: 10,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Sign-ups, logins, checkouts and contact pages are all forms, and they are where users convert or give up. Good forms are clear, work well on mobile keyboards, explain errors and stay secure. Machine coding interviews often ask you to build one." },

      { type: "h2", text: "The mental model: a paper form" },
      { type: "p", text: "Each field has a **label** (the printed question), an **input** (the blank space), a **name** (how the office files your answer) and **rules** (“must be 10 digits”). The `<form>` is the envelope, and submitting posts it to the address in `action`." },

      { type: "h2", text: "Anatomy of a good form" },
      { type: "code", language: "html", code: `<form action="/api/signup" method="post" novalidate>
  <div>
    <label for="name">Full name</label>
    <input id="name" name="name" type="text" autocomplete="name" required />
  </div>

  <div>
    <label for="email">Email</label>
    <input id="email" name="email" type="email" autocomplete="email" required />
  </div>

  <div>
    <label for="phone">Phone</label>
    <input id="phone" name="phone" type="tel" inputmode="numeric"
           pattern="[0-9]{10}" aria-describedby="phone-hint" required />
    <small id="phone-hint">10 digits, no spaces</small>
  </div>

  <fieldset>
    <legend>Preferred batch</legend>
    <label><input type="radio" name="batch" value="weekday" checked /> Weekday</label>
    <label><input type="radio" name="batch" value="weekend" /> Weekend</label>
  </fieldset>

  <button type="submit">Create account</button>
</form>` },
      { type: "table", headers: ["Attribute", "What it does"], rows: [
        ["`for` / `id`", "Links a label to its input. Clicking the label focuses the input, and screen readers read the label."],
        ["`name`", "The key the server receives, for example `email=asha@x.com`. Fields without a `name` are not submitted."],
        ["`type`", "Changes the mobile keyboard and built-in validation: `email`, `tel`, `number`, `date`, `password`, `url`."],
        ["`autocomplete`", "Lets browsers and password managers fill fields correctly, which saves users a lot of typing."],
        ["`aria-describedby`", "Connects hint or error text to the input so screen readers read it."],
        ["`fieldset` / `legend`", "Groups related controls, such as radio buttons, under one question."]
      ] },

      { type: "h2", text: "Built-in validation" },
      { type: "table", headers: ["Attribute", "Example", "Checks"], rows: [
        ["`required`", "`<input required>`", "The field isn’t empty"],
        ["`minlength` / `maxlength`", "`minlength=\"8\"`", "Text length"],
        ["`min` / `max` / `step`", "`type=\"number\" min=\"1\" max=\"10\"`", "Numeric range"],
        ["`pattern`", "`pattern=\"[0-9]{6}\"`", "Matches a regular expression (here, an Indian PIN code)"],
        ["`type`", "`type=\"email\"`", "Basic format of an email or URL"]
      ] },
      { type: "p", text: "You can combine this with JavaScript for custom messages and inline errors using the Constraint Validation API:" },
      { type: "code", language: "javascript", code: `const form = document.querySelector("form");

form.addEventListener("submit", (event) => {
  event.preventDefault();

  if (!form.checkValidity()) {
    const firstInvalid = form.querySelector(":invalid");
    firstInvalid.focus();
    return;
  }

  const data = Object.fromEntries(new FormData(form));
  console.log(data);
  // { name: "Asha", email: "asha@x.com", phone: "9876543210", batch: "weekday" }
});` },

      { type: "h2", text: "Showing errors well" },
      { type: "ul", items: [
        "Show the error next to the field, in text, not only as a red border, because colour alone isn’t enough.",
        "Say how to fix it: “Enter a 10-digit phone number”, not just “Invalid input”.",
        "Validate when the user leaves a field or submits the form, not on every keystroke while they are still typing.",
        "On submit, move focus to the first invalid field.",
        "Mark invalid fields with `aria-invalid=\"true\"` and link the message using `aria-describedby`."
      ] },
      { type: "code", language: "html", code: `<label for="pin">PIN code</label>
<input id="pin" name="pin" aria-invalid="true" aria-describedby="pin-error" />
<p id="pin-error" class="error">Enter a 6-digit PIN code, e.g. 560001</p>` },

      { type: "callout", variant: "warning", title: "Never trust the browser", text: "Anyone can bypass HTML validation by editing the page in DevTools or calling your API directly. Client-side validation is for user experience. Always validate again on the server." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Placeholder used as a label", bad: `<input type="email" placeholder="Email" />`, goodLabel: "A visible label", good: `<label for="email">Email</label>
<input id="email" type="email"
       placeholder="you@example.com" />`, text: "Placeholders disappear when the user starts typing, often have low contrast, and aren’t reliably read as labels by screen readers." },
      { type: "compare", badLabel: "Implicit button type", bad: `<form>
  <button onclick="showPreview()">Preview</button>
  <button>Submit</button>
</form>`, goodLabel: "Explicit button types", good: `<form>
  <button type="button" onclick="showPreview()">Preview</button>
  <button type="submit">Submit</button>
</form>`, text: "Buttons inside a form default to `type=\"submit\"`, so the Preview button would submit the form." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Why is `<label>` important?", answer: ["It gives the input an accessible name that screen readers announce, and it makes the label clickable, which creates a bigger target on mobile. Connect them with `for` and `id`, or wrap the input inside the label."] },
      { type: "qa", question: "What is the difference between GET and POST for forms?", answer: ["GET puts the form data in the URL query string. It is bookmarkable and cached, which suits searches and filters. POST sends the data in the request body, which suits creating or changing data, larger payloads and sensitive values. Sensitive data still needs HTTPS."] },
      { type: "qa", question: "Is client-side validation enough?", answer: ["No. It improves the user experience, but it can be bypassed easily. The server must validate and sanitise every input, because requests can be sent without your form."] },

      { type: "exercise", title: "Build an accessible login form", task: "Create a login form with email and password fields. Requirements: visible labels, the correct input types and `autocomplete` values, a password of at least 8 characters, a hint under the password linked with `aria-describedby`, and a submit button.", language: "html", solution: `<form action="/api/login" method="post">
  <label for="login-email">Email</label>
  <input id="login-email" name="email" type="email"
         autocomplete="username" required />

  <label for="login-password">Password</label>
  <input id="login-password" name="password" type="password"
         autocomplete="current-password" minlength="8"
         aria-describedby="password-hint" required />
  <small id="password-hint">At least 8 characters</small>

  <button type="submit">Log in</button>
</form>`, explanation: "`autocomplete=\"username\"` and `\"current-password\"` tell password managers exactly what to fill in. The `type=\"password\"` input masks the characters. `minlength` and `required` give instant feedback, and the server still validates the credentials." },

      { type: "summary", items: [
        "Every input needs a visible `<label>` linked with `for`/`id`, plus a `name` for submission.",
        "Choose the right `type` and `autocomplete` for better mobile keyboards and autofill.",
        "Use built-in validation attributes, and add JavaScript for custom, inline error messages.",
        "Write errors as fix-it instructions, and link them to inputs with `aria-describedby`.",
        "Client-side validation is for user experience. Always validate on the server."
      ] }
    ]
  },

  {
    slug: "images-media-performance",
    title: "Images, Media and Performance",
    summary: "Serve the right image to every device, embed audio and video properly, and keep pages fast.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Images are usually the heaviest part of a web page. A single unoptimised 4 MB hero photo can make a page feel broken on a phone network. Getting images and media right is one of the biggest performance wins available, and Google measures it through Core Web Vitals." },

      { type: "h2", text: "The mental model: deliver the right size to the right screen" },
      { type: "p", text: "Think of a tailor. Sending everyone an XXL shirt “to be safe” wastes cloth and looks wrong. In the same way, sending a 2400px image to a 360px phone wastes data and slows the page. HTML lets you offer several sizes and lets the browser pick the best one." },

      { type: "h2", text: "The basic image, done right" },
      { type: "code", language: "html", code: `<img
  src="/images/campus-800.jpg"
  alt="Students pair-programming in the Codeazy lab"
  width="800"
  height="533"
  loading="lazy"
  decoding="async"
/>` },
      { type: "table", headers: ["Attribute", "Why"], rows: [
        ["`alt`", "Accessibility, SEO, and shown if the image fails to load"],
        ["`width` / `height`", "Lets the browser reserve space before the image loads, which prevents layout shift (CLS)"],
        ["`loading=\"lazy\"`", "Delays off-screen images until the user scrolls near them"],
        ["`decoding=\"async\"`", "Decodes the image off the main thread where possible"]
      ] },
      { type: "callout", variant: "warning", text: "Never lazy-load the main hero image at the top of the page. It delays your Largest Contentful Paint (LCP). Lazy-load only images below the fold, and consider `fetchpriority=\"high\"` on the hero image." },

      { type: "h2", text: "Responsive images with srcset and sizes" },
      { type: "code", language: "html", code: `<img
  src="/images/hero-800.jpg"
  srcset="/images/hero-400.jpg 400w,
          /images/hero-800.jpg 800w,
          /images/hero-1600.jpg 1600w"
  sizes="(max-width: 600px) 100vw, 50vw"
  alt="Mentor explaining system design on a whiteboard"
  width="1600"
  height="900"
/>` },
      { type: "ol", items: [
        "`srcset` lists the available files and their real widths (`w` means pixels wide).",
        "`sizes` tells the browser how wide the image will be displayed: full width on screens up to 600px, and half the viewport width otherwise.",
        "The browser combines that with the screen’s pixel density and chooses the smallest file that still looks sharp. A 360px phone at 2x density needs about 720px, so it downloads the 800w file instead of the 1600w one."
      ] },

      { type: "h2", text: "Modern formats and art direction with picture" },
      { type: "code", language: "html", code: `<picture>
  <!-- Art direction: a cropped image for small screens -->
  <source media="(max-width: 600px)" srcset="/images/team-square.avif" type="image/avif" />

  <!-- Modern formats first, with a fallback -->
  <source srcset="/images/team.avif" type="image/avif" />
  <source srcset="/images/team.webp" type="image/webp" />
  <img src="/images/team.jpg" alt="The Codeazy mentor team" width="1200" height="600" />
</picture>` },
      { type: "table", headers: ["Format", "Best for", "Notes"], rows: [
        ["AVIF", "Photos", "Smallest files, widely supported in modern browsers"],
        ["WebP", "Photos and graphics", "Much smaller than JPEG/PNG, near-universal support"],
        ["JPEG", "Photos (fallback)", "Universal, larger"],
        ["PNG", "Screenshots, images needing transparency", "Lossless and often large"],
        ["SVG", "Logos, icons, illustrations", "Vector, so it is sharp at any size and tiny"]
      ] },

      { type: "h2", text: "figure and figcaption" },
      { type: "code", language: "html", code: `<figure>
  <img src="/diagrams/event-loop.svg" alt="Diagram of the call stack, microtask queue and macrotask queue" />
  <figcaption>The event loop drains all microtasks before the next macrotask.</figcaption>
</figure>` },

      { type: "h2", text: "Video and audio" },
      { type: "code", language: "html", code: `<video controls width="960" height="540" preload="metadata"
       poster="/videos/intro-poster.jpg">
  <source src="/videos/intro.webm" type="video/webm" />
  <source src="/videos/intro.mp4" type="video/mp4" />
  <track kind="captions" src="/videos/intro.en.vtt" srclang="en" label="English" default />
  Your browser doesn't support video. <a href="/videos/intro.mp4">Download the video</a>.
</video>

<audio controls src="/podcast/episode-12.mp3"></audio>` },
      { type: "ul", items: [
        "`controls` shows the play, pause and volume controls. Never hide them without providing your own accessible ones.",
        "`preload=\"metadata\"` loads only the duration and dimensions, not the whole video.",
        "`poster` is shown before playback starts.",
        "`<track kind=\"captions\">` adds subtitles, which matter for accessibility and for people watching on mute.",
        "Autoplay only works when the video is `muted`, and should be used rarely."
      ] },

      { type: "h2", text: "Embedding third-party content" },
      { type: "code", language: "html", code: `<iframe
  src="https://www.youtube-nocookie.com/embed/VIDEO_ID"
  title="System Design basics - Codeazy"
  width="560" height="315"
  loading="lazy"
  allow="accelerometer; encrypted-media; picture-in-picture"
  allowfullscreen>
</iframe>` },
      { type: "p", text: "Give every iframe a `title` so screen readers can describe it. Lazy-load it, because each embedded player loads a lot of JavaScript." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "No dimensions", bad: `<img src="banner.jpg" alt="Sale banner" />
<!-- content jumps down when it loads -->`, goodLabel: "Space reserved", good: `<img src="banner.jpg" alt="Sale banner"
     width="1200" height="400" />

/* CSS keeps it responsive */
img { max-width: 100%; height: auto; }` },
      { type: "compare", badLabel: "Huge file, shrunk with CSS", bad: `<!-- 4000px, 3.8 MB photo -->
<img src="photo-original.jpg"
     style="width: 300px" alt="..." />`, goodLabel: "Right-sized and modern", good: `<img src="photo-600.webp"
     srcset="photo-300.webp 300w, photo-600.webp 600w"
     sizes="300px" width="300" height="200" alt="..." />` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How do `srcset` and `sizes` work?", answer: ["`srcset` lists image candidates with their intrinsic widths. `sizes` describes how wide the image will be displayed at different viewport sizes. The browser combines the display width with the device pixel ratio and picks the smallest candidate that is large enough."] },
      { type: "qa", question: "When would you use `<picture>` instead of `srcset`?", answer: ["Use `<picture>` for art direction, meaning a differently cropped image at different breakpoints, and for format fallbacks such as AVIF, then WebP, then JPEG. For simply serving different sizes of the same image, `srcset` with `sizes` is enough."] },
      { type: "qa", question: "What is Cumulative Layout Shift (CLS) and how do images cause it?", answer: ["CLS measures how much visible content moves unexpectedly while a page loads. Images without dimensions load and push content down. Setting `width` and `height` (or CSS `aspect-ratio`) lets the browser reserve the space in advance."] },
      { type: "qa", question: "Should you lazy-load every image?", answer: ["No. Lazy-load images below the fold. The main above-the-fold image (often the LCP element) should load eagerly, ideally with `fetchpriority=\"high\"`, otherwise lazy loading delays it and hurts LCP."] },
      { type: "qa", question: "Which image format would you choose for a logo?", answer: ["SVG. It is a vector format, so it stays sharp at any size, is usually tiny, and can be styled with CSS. Use PNG or WebP only if the logo is a complex raster image."] },

      { type: "exercise", title: "Optimise a hero image", task: "A landing page shows a full-width hero image on mobile and a half-width one on desktops (above 900px). You have `hero-480.webp`, `hero-960.webp` and `hero-1920.webp` (aspect ratio 16:9) plus a JPEG fallback `hero-960.jpg`. Write the markup so it loads quickly, avoids layout shift and is accessible.", language: "html", solution: `<picture>
  <source
    type="image/webp"
    srcset="/img/hero-480.webp 480w, /img/hero-960.webp 960w, /img/hero-1920.webp 1920w"
    sizes="(min-width: 900px) 50vw, 100vw" />
  <img
    src="/img/hero-960.jpg"
    alt="Learners collaborating on a coding project"
    width="1920" height="1080"
    fetchpriority="high"
    decoding="async" />
</picture>`, explanation: "`sizes` mirrors the layout: half-width above 900px and full-width otherwise. It is the hero, so it is **not** lazy-loaded and gets `fetchpriority=\"high\"`. The width and height give the 16:9 ratio, so space is reserved before the image arrives." },

      { type: "summary", items: [
        "Images are the heaviest assets, so right-sizing them is a major performance win.",
        "Always set `alt`, `width` and `height`. Lazy-load only below-the-fold images.",
        "`srcset` and `sizes` let the browser pick the best file. `<picture>` adds format fallbacks and art direction.",
        "Prefer AVIF or WebP for photos and SVG for logos and icons.",
        "Video needs `controls`, a `poster`, `preload=\"metadata\"` and captions, and every iframe needs a `title`."
      ] }
    ]
  },

  {
    slug: "seo-and-meta",
    title: "Meta Tags, SEO and Social Sharing",
    summary: "Help search engines understand your pages and make your links look great when shared.",
    readTime: 9,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Your page might be excellent, but if search engines can’t understand it and shared links show a blank preview, few people will find it. Most of this comes down to a handful of tags in `<head>` and good HTML structure." },

      { type: "h2", text: "The mental model: a book’s cover and blurb" },
      { type: "p", text: "Search results and link previews are your page’s cover. The `<title>` is the book title, the meta description is the blurb on the back, and the Open Graph image is the cover art. You don’t control the ranking directly, but you do control how the page is presented and understood." },

      { type: "h2", text: "Essential head tags" },
      { type: "code", language: "html", code: `<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />

  <!-- Search results -->
  <title>JavaScript Closures Explained with Examples | Codeazy</title>
  <meta name="description"
        content="Learn JavaScript closures with simple analogies, real-world examples, interview questions and practice exercises." />
  <link rel="canonical" href="https://codeazy.in/learn/javascript/closures" />

  <!-- Social sharing (Open Graph is used by WhatsApp, LinkedIn, Facebook) -->
  <meta property="og:title" content="JavaScript Closures Explained" />
  <meta property="og:description" content="Analogies, examples and interview questions." />
  <meta property="og:image" content="https://codeazy.in/og/closures.png" />
  <meta property="og:url" content="https://codeazy.in/learn/javascript/closures" />
  <meta property="og:type" content="article" />

  <!-- X / Twitter -->
  <meta name="twitter:card" content="summary_large_image" />
</head>` },
      { type: "table", headers: ["Tag", "Purpose", "Good practice"], rows: [
        ["`<title>`", "Clickable headline in search results", "Unique per page, about 50–60 characters, most important words first"],
        ["`meta description`", "Snippet under the title", "About 150–160 characters, a clear summary that invites the click"],
        ["`canonical`", "Says which URL is the “real” version", "Prevents duplicate-content issues from `?ref=` parameters"],
        ["`og:*`", "Link previews in chats and social apps", "A 1200×630 image, and a title and description written for sharing"],
        ["`robots`", "Indexing instructions", "`noindex` for admin, thank-you and search-result pages"]
      ] },

      { type: "h2", text: "Content structure is SEO too" },
      { type: "ul", items: [
        "One clear `<h1>` that matches what people search for.",
        "Descriptive headings (`<h2>`, `<h3>`) that outline the page.",
        "Descriptive link text: “Read the closures lesson”, not “click here”.",
        "Alt text on meaningful images.",
        "Readable, human-friendly URLs: `/learn/javascript/closures`, not `/page?id=482`."
      ] },

      { type: "h2", text: "Structured data" },
      { type: "p", text: "Structured data uses JSON-LD to describe your content in a format search engines understand, which can unlock rich results such as course listings, FAQs and article dates:" },
      { type: "code", language: "html", code: `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Course",
  "name": "System Design",
  "description": "Design scalable systems: caching, sharding, queues and case studies.",
  "provider": {
    "@type": "Organization",
    "name": "Codeazy",
    "sameAs": "https://codeazy.in"
  }
}
</script>` },

      { type: "h2", text: "Single-page apps and SEO" },
      { type: "p", text: "In a client-rendered React app, the server sends an almost empty HTML file and JavaScript builds the page. Google can run JavaScript, but more slowly and less reliably, and most link-preview bots can’t run it at all. This is why content-heavy sites use **server-side rendering** or **static generation** (for example Next.js), so every page arrives as complete HTML with its own meta tags." },
      { type: "callout", variant: "tip", text: "Page speed is a ranking signal too. Google’s Core Web Vitals measure loading (LCP), responsiveness (INP) and visual stability (CLS). Image sizes, `defer` scripts and reserved image dimensions all help." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Same title everywhere", bad: `<!-- every page -->
<title>Codeazy</title>`, goodLabel: "Unique, descriptive titles", good: `<title>Binary Search Explained | DSA | Codeazy</title>
<title>System Design Interview Framework | Codeazy Blog</title>` },
      { type: "compare", badLabel: "Vague link text", bad: `To learn more about closures,
<a href="/learn/javascript/closures">click here</a>.`, goodLabel: "Descriptive link text", good: `Read our
<a href="/learn/javascript/closures">guide to JavaScript closures</a>.` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is a canonical URL?", answer: ["A `<link rel=\"canonical\">` tag tells search engines the preferred URL for a page when the same content is reachable at several URLs, for example with tracking parameters. Search engines then consolidate ranking signals on that one URL."] },
      { type: "qa", question: "Why do client-side rendered apps struggle with SEO?", answer: ["The initial HTML has almost no content, because JavaScript renders it later. Crawlers have to run JavaScript, which is slower and less reliable, and many social preview bots don’t run it at all. SSR or static generation sends complete HTML with page-specific meta tags."] },
      { type: "qa", question: "What are Open Graph tags?", answer: ["Meta tags such as `og:title`, `og:description` and `og:image` that control how a link appears when shared on platforms like WhatsApp, LinkedIn and Facebook."] },

      { type: "exercise", title: "Write the head for a blog post", task: "Write the `<head>` for a blog post titled “10 DSA Patterns That Solve Most Interview Problems” at `https://codeazy.in/blog/dsa-patterns-for-interviews`. Include the title, description, canonical URL and Open Graph tags.", language: "html", solution: `<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>10 DSA Patterns for Coding Interviews | Codeazy</title>
  <meta name="description"
        content="Stop memorising problems. Learn the 10 DSA patterns behind most coding interview questions, with examples and practice tips." />
  <link rel="canonical" href="https://codeazy.in/blog/dsa-patterns-for-interviews" />
  <meta property="og:title" content="10 DSA Patterns That Solve Most Interview Problems" />
  <meta property="og:description" content="Learn the patterns, not hundreds of problems." />
  <meta property="og:image" content="https://codeazy.in/og/dsa-patterns.png" />
  <meta property="og:url" content="https://codeazy.in/blog/dsa-patterns-for-interviews" />
  <meta property="og:type" content="article" />
  <meta name="twitter:card" content="summary_large_image" />
</head>` },

      { type: "summary", items: [
        "Give every page a unique `<title>` and meta description, because they are your search result.",
        "Use `canonical` to avoid duplicate URLs, and Open Graph tags for rich link previews.",
        "Clean structure helps: one `<h1>`, descriptive headings, links and URLs.",
        "Structured data (JSON-LD) can unlock rich results.",
        "Client-rendered SPAs are weak at SEO, and SSR or static generation solves this."
      ] }
    ]
  }
];
